import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useTodoLists } from '../hooks';
import { useTodoList } from '../hooks/useTodoList';
import { ListCard, CreateListDialog } from '../components';
import api from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';

/**
 * Holder-scoped list dashboard. The Holder switcher (Personal + each
 * active Org Membership) lives in the page header; switching Holders
 * is how the user moves between personal and team todo lists. There is
 * no separate "shared with me" view — Org Holder membership IS the
 * sharing model in W6.
 */
export function MyListsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedHolderId, isLoading: switcherLoading } = useHolderSwitcher();
  const { lists, loading, error } = useTodoLists(selectedHolderId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Reuse useTodoList only for its strong-typed update path; we don't
  // need it here. Just typecheck-ping it.
  void useTodoList;

  const handleCreate = async (input: { name: string; description?: string }) => {
    if (!selectedHolderId) return;
    const result = await api.createTodoList(selectedHolderId, input);
    await queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my(selectedHolderId) });
    setCreateDialogOpen(false);
    navigate(`/a/arche_todo_lists/${result.list.listId}`);
  };

  if (switcherLoading) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-muted-foreground">
            Select a workspace to view your todo lists.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_todo_lists" title="Todo Lists" />
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_todo_lists" title="Todo Lists">
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New List
        </Button>
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 space-y-6 w-full">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : lists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ListTodo className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground mb-4">No todo lists yet</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first list
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.map((list) => (
              <ListCard
                key={list.listId}
                list={list}
                onClick={() => navigate(`/a/arche_todo_lists/${list.listId}`)}
              />
            ))}
          </div>
        )}

        <CreateListDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}
